/**
 * MockupTemplateBulkUploadModal — picker pour import en masse de templates PSD.
 *
 * **Réduit au picker depuis 2026-05-09.** Auparavant la modal hébergeait
 * aussi la phase upload + done — d'où le bouton « Fermer (les uploads en
 * cours continueront) » qui en réalité annulait le pool au démontage
 * (incident 2026-05-09). Désormais : la modal collecte (kind + files),
 * `Importer` enqueue dans `<BulkUploadStackProvider>` (pile globale au
 * niveau AppShell, style Google Drive) puis ferme. La progression vit
 * dans l'overlay bas-droit, qui survit aux navigations.
 *
 * **PSD only** : on n'accepte plus l'upload d'images plates (l'usage
 * industriel est en PSD avec mask_zone auto-extraite).
 *
 * **Idempotence garantie** : si l'utilisatrice navigue ailleurs / ferme
 * un onglet pendant l'upload, les requêtes en flight finissent côté
 * serveur (FastAPI ne sait pas que le client a quitté). Re-drag le même
 * set : les déjà-uploadés tomberont en `duplicate_content` (succès
 * silencieux), les manquants seront créés.
 */

import { useState } from 'react'
import { Plus } from 'lucide-react'

import Button from '@/components/ui/Button'
import FileDropZone from '@/components/ui/FileDropZone'
import Modal from '@/components/ui/Modal'
import Select from '@/components/ui/Select'
import { _mapErrorToStatus } from '@/components/upload/_mapErrorToStatus'
import { useBulkUploadStack } from '@/contexts/bulk-upload-stack-context'
import { mockupTemplatesApi } from '@/utils/api/mockup-templates'
import { useQueryClient } from '@tanstack/react-query'
import { mockupTemplateKeys } from '@/hooks/useMockupTemplates'
import {
  MOCKUP_TEMPLATE_KIND_FR,
  MOCKUP_TEMPLATE_KIND_OPTIONS,
  type MockupTemplateKind,
} from '@/types/mockup-templates'

import './MockupTemplateBulkUploadModal.css'

const BULK_ACCEPT = '.psd,image/vnd.adobe.photoshop'
const BULK_MAX_FILES = 1000

/**
 * Plafond de requêtes simultanées vers le BE (passé tel quel à
 * `enqueue`). À 2, on garde une marge bande passante upload (PSD ~50-150 Mo)
 * et CPU BE (psd-tools/Pillow CPU-bound). Le timeout axios est override à
 * 5 min côté `mockupTemplatesApi.create`.
 */
const CONCURRENCY_LIMIT = 2

interface MockupTemplateBulkUploadModalProps {
  isOpen: boolean
  onClose: () => void
}

export default function MockupTemplateBulkUploadModal({
  isOpen,
  onClose,
}: MockupTemplateBulkUploadModalProps) {
  const [files, setFiles] = useState<File[]>([])
  const [kind, setKind] = useState<MockupTemplateKind>('lifestyle_room')
  const { enqueue } = useBulkUploadStack()
  const qc = useQueryClient()

  const handleClose = () => {
    setFiles([])
    setKind('lifestyle_room')
    onClose()
  }

  const handleImport = () => {
    if (files.length === 0) return
    enqueue({
      label: `Templates ${MOCKUP_TEMPLATE_KIND_FR[kind]} — ${files.length} PSD${files.length > 1 ? 's' : ''}`,
      files,
      uploader: (file) => mockupTemplatesApi.create({ file, kind }),
      concurrencyLimit: CONCURRENCY_LIMIT,
      mapError: _mapErrorToStatus,
      onComplete: () => {
        // Invalide une seule fois en fin de batch — la grille `/mockups`
        // se rafraîchit avec tous les nouveaux templates en un refetch.
        void qc.invalidateQueries({ queryKey: mockupTemplateKeys.all })
      },
    })
    handleClose()
  }

  const totalSizeMB = files.reduce((acc, f) => acc + f.size, 0) / (1024 * 1024)

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Ajouter des templates"
      kicker="Mockups · PSD seulement"
      size="large"
    >
      <Modal.Body>
        <div className="bulk-upload-pick">
          <p className="bulk-upload-intro">
            Glisse jusqu'à {BULK_MAX_FILES} fichiers <strong>.psd</strong>. Les uploads partent en
            parallèle ({CONCURRENCY_LIMIT} requêtes simultanées, ~1.5s/PSD côté serveur). Le{' '}
            <code>name</code> est dérivé du nom de fichier — tu pourras renommer ensuite. Vision
            Tier 1 tourne en arrière-plan via le scheduler.
          </p>
          <p className="bulk-upload-intro">
            <strong>Tu peux fermer cette fenêtre dès que tu as cliqué « Importer »</strong> — les
            uploads continuent en arrière-plan dans la barre en bas à droite. Tu peux changer de
            page, voire fermer l'onglet (les fichiers en flight finissent côté serveur — re-drag le
            même set tombera en « déjà existant »).
          </p>

          <div className="bulk-upload-controls">
            <label className="bulk-upload-control">
              <span className="bulk-upload-control-label">Type pour tout le batch</span>
              <Select value={kind} onChange={(e) => setKind(e.target.value as MockupTemplateKind)}>
                {MOCKUP_TEMPLATE_KIND_OPTIONS.map((k) => (
                  <option key={k} value={k}>
                    {MOCKUP_TEMPLATE_KIND_FR[k]}
                  </option>
                ))}
              </Select>
              <span className="bulk-upload-control-hint">
                Si tu mélanges plusieurs types, fais un batch par type.
              </span>
            </label>
          </div>

          <FileDropZone
            value={files}
            onChange={setFiles}
            accept={BULK_ACCEPT}
            maxFiles={BULK_MAX_FILES}
            ariaLabel="Glisser-déposer des PSDs ici"
          />

          {files.length > 0 ? (
            <p className="bulk-upload-summary">
              {files.length} fichier{files.length > 1 ? 's' : ''} sélectionné
              {files.length > 1 ? 's' : ''} · {totalSizeMB.toFixed(0)} Mo total
            </p>
          ) : null}
        </div>
      </Modal.Body>

      <Modal.Footer>
        <Button variant="ghost" onClick={handleClose}>
          Annuler
        </Button>
        <Button variant="primary" onClick={handleImport} disabled={files.length === 0}>
          <Plus size={14} aria-hidden="true" />
          Importer{files.length > 0 ? ` (${files.length})` : ''}
        </Button>
      </Modal.Footer>
    </Modal>
  )
}
